import * as THREE from 'three';
import { TrackElement } from './TrackElement';
import { TrainingTrackElement } from '../../../../../shared/src/trainingTrack.types';
import { TRAINING_TRACK_CONFIG } from '../config/TrainingTrackConfig';

/**
 * Class representing a ramp element in the training track
 */
export class RampElement extends TrackElement {
    private rampMesh: THREE.Mesh | null = null;
    private rampHeight: number;
    private triggerSlowMotion: boolean;
    
    /**
     * Constructor
     * @param element The ramp element data
     */
    constructor(element: TrainingTrackElement) {
        super(element);
        this.rampHeight = element.properties?.rampHeight || 2;
        this.triggerSlowMotion = element.properties?.triggerSlowMotion || false;
    }
    
    /**
     * Creates the Three.js object for this ramp
     * @returns The created ramp object
     */
    protected createObject(): THREE.Object3D {
        // Tworzymy grupę dla rampy i efektów
        const rampGroup = new THREE.Group();
        
        // Tworzymy rampę z bardziej zaawansowaną geometrią
        const geometry = new THREE.BoxGeometry(this.element.scale * 2, this.rampHeight, this.element.scale * 4);
        const material = new THREE.MeshStandardMaterial({
            color: TRAINING_TRACK_CONFIG.RAMP.COLOR,
            transparent: true,
            opacity: TRAINING_TRACK_CONFIG.RAMP.OPACITY,
            emissive: TRAINING_TRACK_CONFIG.RAMP.COLOR,
            emissiveIntensity: TRAINING_TRACK_CONFIG.RAMP.GLOW_INTENSITY,
            metalness: 0.6,
            roughness: 0.3
        });
        
        this.rampMesh = new THREE.Mesh(geometry, material);
        this.rampMesh.castShadow = true;
        this.rampMesh.receiveShadow = true;
        
        // Dostosuj pozycję, aby uwzględnić wysokość rampy
        this.rampMesh.position.y = this.rampHeight / 2;
        
        // Obróć, aby stworzyć nachylenie
        this.rampMesh.rotation.x = -Math.PI / 6; // Bardziej strome nachylenie
        
        // Dodaj rampę do grupy
        rampGroup.add(this.rampMesh);
        
        // Dodaj efekt świetlny dla rampy
        const rampLight = new THREE.PointLight(
            TRAINING_TRACK_CONFIG.RAMP.COLOR,
            1.5,
            10
        );
        rampLight.position.set(0, this.rampHeight + 1, 0);
        rampGroup.add(rampLight);
        
        // Dodaj efekt cząsteczkowy na górze rampy
        if (TRAINING_TRACK_CONFIG.RAMP.TRAIL_EFFECT) {
            const particleCount = 50;
            const particleGeometry = new THREE.BufferGeometry();
            const particleMaterial = new THREE.PointsMaterial({
                color: TRAINING_TRACK_CONFIG.RAMP.COLOR,
                size: 0.2,
                transparent: true,
                opacity: 0.7,
                blending: THREE.AdditiveBlending
            });
            
            // Utwórz pozycje cząsteczek wzdłuż górnej krawędzi rampy
            const particlePositions = new Float32Array(particleCount * 3);
            for (let i = 0; i < particleCount; i++) {
                const xPos = (Math.random() - 0.5) * this.element.scale * 1.8;
                particlePositions[i * 3] = xPos;
                particlePositions[i * 3 + 1] = this.rampHeight * 0.8 + Math.random() * 0.5;
                particlePositions[i * 3 + 2] = -this.element.scale * 1.5 + Math.random() * 0.5;
            }
            
            particleGeometry.setAttribute('position', new THREE.BufferAttribute(particlePositions, 3));
            const particles = new THREE.Points(particleGeometry, particleMaterial);
            rampGroup.add(particles);
        }
        
        return rampGroup;
    }
    
    /**
     * Handles player interaction with this ramp
     * @param playerPosition The player's position
     * @param playerTank The player's tank
     * @param distance The distance to the player
     * @returns True if interaction was handled, false otherwise
     */
    public handleInteraction(playerPosition: THREE.Vector3, playerTank: any, distance: number): boolean {
        if (!this.triggerSlowMotion) return false;
        
        // Check if player is on the ramp
        if (distance < this.element.scale * 2) {
            // Apply slow motion effect to player's tank if it has the method
            if (playerTank.applySlowMotion) {
                playerTank.applySlowMotion(0.5, 2000); // 50% speed for 2 seconds
                console.log('[Ramp] Applying slow motion effect to player');
                return true;
            } else {
                console.log('[Ramp] Player tank does not have applySlowMotion method');
            }
        }
        
        return false;
    }
    
    /**
     * Gets whether this ramp triggers slow motion
     * @returns True if this ramp triggers slow motion, false otherwise
     */
    public doesTriggerSlowMotion(): boolean {
        return this.triggerSlowMotion;
    }
    
    /**
     * Gets the height of the ramp
     * @returns The ramp height
     */
    public getRampHeight(): number {
        return this.rampHeight;
    }
}